import { inject, Injectable, signal } from '@angular/core';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from '../config/auth.config';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { ProfileService } from './profile.service';
import { catchError, EMPTY, lastValueFrom, of, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountsService } from './accounts.service';
import { AlloyService } from './alloy.service';
import { environment } from '../../../environments/environment';
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private oauthService: OAuthService = inject(OAuthService);
  private profileSv: ProfileService = inject(ProfileService);
  private accountsService: AccountsService = inject(AccountsService);
  private router: Router = inject(Router);
  private alloyService: AlloyService = inject(AlloyService);

  public hasSession = signal(false);
  public oauthEvents = signal<OAuthEvent | null>(null);
  private refreshLifecycleInitialized = false;

  constructor() {
    this.oauthService.events.subscribe(e => this.oauthEvents.set(e));
  }

  async initAuth(): Promise<void> {
    const alloyInitPromise = this.alloyService.initAlloyOnce();
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage);
    this.setupRefreshLifecycle();
    try {
      // Methods loadDiscoveryDocument() and tryLogin() can be replaced with method loadDiscoveryDocumentAndTryLogin instead.
      // await this.oauthService.loadDiscoveryDocument();
      // const loggedIn = await this.oauthService.tryLogin();
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      if (this.oauthService.hasValidAccessToken()) {
        // const { sub, name, email } = this.oauthService.getIdentityClaims();
        // this.accountsService.setAccount = { sub, name, email };
        // console.log('getIdentityClaims', this.oauthService.getIdentityClaims());
        const expiration = this.oauthService.getAccessTokenExpiration();
        const now = Date.now();
        const timeLeft = expiration - now;
        console.log('Expiration', timeLeft);

        // We set the oauth event to the signal
        // console.log('logoutUrl ', this.oauthService.logoutUrl);
        // console.log('idToken', this.oauthService.getIdToken());
        // console.log(this.oauthService.getAccessToken());
        // console.log(this.oauthService.getRefreshToken());
        // console.log('getIdToken => ', this.oauthService.getIdToken());
        // console.log(this.oauthService.getAccessTokenExpiration);
        // console.log(this.oauthService.getGrantedScopes);
        this.hasSession.set(true);
        const userId = this.setUserId(this.oauthService.getAccessToken());
        localStorage.setItem('user_id', userId);

        const tmxSessionId = await alloyInitPromise;
        console.log(
          '[AuthService] TMX initialized:',
          Boolean(tmxSessionId)
        );
        console.log(tmxSessionId);
        

        if (!tmxSessionId) {
          console.warn(
            '[AuthService] TMX session ID was not generated.'
          );
        }
        await this.loadAndSetUserProfile();
        if (window.location.pathname.includes("/callback")) {
          this.router.navigate(["/"])
        }
      } else {
        this.hasSession.set(false);
        await alloyInitPromise;
        this.login();
      }
    } catch (err) {
      console.log('loadDiscoveryDocument => ', err);
    }

  }

  private setupRefreshLifecycle(): void {
    if (this.refreshLifecycleInitialized) {
      return;
    }

    this.refreshLifecycleInitialized = true;
    this.oauthService.setupAutomaticSilentRefresh();

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      if (event.type === 'token_received') {
        console.info('[AuthService] token received/refreshed successfully.');
      }

      if (event.type === 'token_refresh_error' || event.type === 'silent_refresh_error') {
        console.warn('[AuthService] token refresh failed.', event);
      }

      if (event.type === 'token_expires') {
        const expiresAt = this.oauthService.getAccessTokenExpiration();
        const timeLeftMs = expiresAt - Date.now();
        console.info('[AuthService] token expiring soon.', { timeLeftMs });
      }
    });

    console.info('[AuthService] automatic silent refresh enabled.');
  }

  login() {
    this.oauthService.initCodeFlow();
  }

  logout() {
    const idToken = this.IdToken;
    const postLogoutRedirectUri = window.location.origin;

    // Clear oauth client local state before redirecting to OP logout.
    this.oauthService.logOut();

    sessionStorage.clear();
    localStorage.clear();

    const issuerBaseUrl = (environment.oauth.issuer || '').replace(/\/+$/, '');
    if (issuerBaseUrl) {
      const params = new URLSearchParams({
        post_logout_redirect_uri: postLogoutRedirectUri,
      });

      if (idToken) {
        params.set('id_token_hint', idToken);
      }

      const logoutUrl = `${issuerBaseUrl}/connect/endSession?${params.toString()}`;
      window.location.assign(logoutUrl);
      return;
    }

    window.location.assign(postLogoutRedirectUri);

    // this.oauthService.revokeTokenAndLogout({
    // id_token_hint: token,
    //   post_logout_redirect_uri: window.location.origin
    // });

    // Manual logout
    // const relayState = encodeURIComponent(window.location.origin); // Redirect back to home page after logout
    // const logoutUrl = `https://openam-day1cloud-use1-sandbox.id.forgerock.io/am/oauth2/alpha/connect/endSession?id_token_hint=${token}&post_logout_redirect_uri=${window.location.origin}`
    // const logoutUrl = `https://risk-detection-router-api-qa-ct5ogq.rajrd4-1.usa-e1.cloudhub.io/ext/oauth2/connect/endSession`      
    // window.location.href = logoutUrl;

    // console.log('logout => ', token);
    // console.log('logoutUrl => ', logoutUrl);       
  }

  get isLogedIn() {
    return this.oauthService.hasValidAccessToken();
  }

  get IdToken() {
    return this.oauthService.getIdToken();
  }

  get userProfile() {
    return this.oauthService.getIdentityClaims() ?? {};
  }

  /**
   * Temporary method to get the userId from decoded JWT
   * Todo: Improve validations...
   * @param token 
   * @returns userId as string
   */
  private setUserId(token: any): string {
    const obj = jwtDecode(token);
    const { sub } = obj;
    return sub as string;
  }

  private async loadAndSetUserProfile() {
    const profileResponse = await lastValueFrom(
      this.profileSv.getUserProfile()
        .pipe(
          catchError((err: HttpErrorResponse) => {
            return of([{}]);
          })
        )
    );
    // We set the API response in the signal
    this.profileSv.setUserProfile = profileResponse.profile.data;
  }
}
